'use client';

import React, { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import PlatformBadge from '@/components/ui/PlatformBadge';
import { MapPin, Star, Users, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import Link from 'next/link';

interface BlowerProfile {
  id: string;
  bio: string;
  tier: string;
  total_followers: number;
  niche: string;
  niches: string[];
  location: string;
}

interface UserProfile {
  full_name: string;
  avatar_url: string | null;
  username: string;
}

interface SocialPlatform {
  id: string;
  platform: string;
  handle: string;
  follower_count: number;
  engagement_rate: number;
  is_verified: boolean;
}

const TIER_COLORS: Record<string, string> = {
  nano: 'bg-muted/50 text-muted-foreground',
  micro: 'bg-primary/15 text-accent',
  mid_tier: 'bg-warning/10 text-warning',
  macro: 'bg-warning/15 text-warning',
  mega: 'bg-success/15 text-success',
};

const TIER_LABELS: Record<string, string> = {
  nano: 'Nano Creator',
  micro: 'Micro Creator',
  mid_tier: 'Mid-tier Creator',
  macro: 'Macro Creator',
  mega: 'Mega Creator',
};

export default function BlowerPublicProfilePage() {
  const params = useParams();
  const username = params?.username as string;
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [blowerProfile, setBlowerProfile] = useState<BlowerProfile | null>(null);
  const [platforms, setPlatforms] = useState<SocialPlatform[]>([]);
  const [completedGigs, setCompletedGigs] = useState(0);
  const supabase = createClient();

  useEffect(() => {
    if (!username) return;
    const fetchProfile = async () => {
      const { data: up } = await supabase
        .from('user_profiles')
        .select('full_name, avatar_url, username, id')
        .eq('username', username)
        .eq('role', 'blower')
        .single();

      if (!up) { setNotFound(true); setLoading(false); return; }
      setUserProfile({ full_name: up.full_name, avatar_url: up.avatar_url, username: up.username });

      const [bpRes, platformsRes, gigsRes] = await Promise.all([
        supabase.from('blower_profiles').select('*').eq('user_id', up.id).single(),
        supabase.from('social_platforms').select('*').eq('user_id', up.id).eq('is_verified', true),
        supabase.from('gigs').select('id', { count: 'exact', head: true }).eq('blower_id', up.id).eq('status', 'paid'),
      ]);

      if (bpRes.data) setBlowerProfile(bpRes.data);
      if (platformsRes.data) setPlatforms(platformsRes.data);
      setCompletedGigs(gigsRes.count || 0);
      setLoading(false);
    };
    fetchProfile();
  }, [username]);

  const initials = userProfile?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  const totalFollowers = platforms.reduce((sum, p) => sum + (p.follower_count || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={32} className="text-primary animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <AppLogo size={120} />
        <h1 className="text-2xl font-700 text-foreground">Profile Not Found</h1>
        <p className="text-muted-foreground">The blower @{username} doesn't exist or hasn't set up their profile.</p>
        <Link href="/" className="btn-primary px-6 py-2.5 rounded-xl text-sm font-600">Go Home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border px-4 sm:px-6 py-4 flex items-center justify-between">
        <AppLogo size={110} className="py-1" />
        <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          Join Blow →
        </Link>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        {/* Profile Card */}
        <div className="bg-card border border-border rounded-2xl p-8 text-center card-glow">
          {userProfile?.avatar_url ? (
            <img
              src={userProfile.avatar_url}
              alt={`${userProfile.full_name} profile photo`}
              className="w-24 h-24 rounded-full object-cover border-2 border-primary/30 mx-auto mb-4"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gradient-purple flex items-center justify-center text-white text-3xl font-700 mx-auto mb-4">
              {initials}
            </div>
          )}
          <h1 className="text-2xl font-700 text-foreground">{userProfile?.full_name}</h1>
          <p className="text-muted-foreground text-sm mt-1">@{username}</p>

          {blowerProfile?.tier && (
            <span className={`inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-600 ${TIER_COLORS[blowerProfile.tier] || 'bg-muted text-muted-foreground'}`}>
              <Star size={11} />
              {TIER_LABELS[blowerProfile.tier] || blowerProfile.tier}
            </span>
          )}

          {blowerProfile?.bio && (
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed max-w-md mx-auto">{blowerProfile.bio}</p>
          )}

          <div className="flex items-center justify-center gap-6 mt-6">
            {blowerProfile?.location && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin size={14} />
                {blowerProfile.location}
              </div>
            )}
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Users size={14} />
              {totalFollowers >= 1000000
                ? `${(totalFollowers / 1000000).toFixed(1)}M`
                : totalFollowers >= 1000
                ? `${(totalFollowers / 1000).toFixed(1)}K`
                : totalFollowers.toLocaleString()} followers
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <CheckCircle size={14} className="text-success" />
              {completedGigs} campaigns
            </div>
          </div>
        </div>

        {/* Niches — blower_profiles.niche is comma-separated TEXT */}
        {(() => {
          const nicheTags = blowerProfile?.niches?.length
            ? blowerProfile.niches
            : String(blowerProfile?.niche || '').split(',').map((n) => n.trim()).filter(Boolean);
          if (nicheTags.length === 0) return null;
          return (
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="font-600 text-foreground mb-3">Content Niches</h3>
            <div className="flex flex-wrap gap-2">
              {nicheTags.map(niche => (
                <span key={niche} className="bg-primary/10 text-accent text-sm px-3 py-1.5 rounded-full font-500">
                  {niche}
                </span>
              ))}
            </div>
          </div>
          );
        })()}

        {/* Connected Platforms */}
        {platforms.length > 0 && (
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="font-600 text-foreground mb-4">Verified Platforms</h3>
            <div className="space-y-3">
              {platforms.map(platform => (
                <div key={platform.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-xl border border-border">
                  <div className="flex items-center gap-3">
                    <PlatformBadge platform={platform.platform as 'instagram' | 'tiktok' | 'youtube' | 'twitter' | 'facebook'} size="md" showLabel />
                    <div>
                      <p className="text-sm font-600 text-foreground">@{platform.handle}</p>
                      <p className="text-xs text-muted-foreground">
                        {platform.follower_count >= 1000000
                          ? `${(platform.follower_count / 1000000).toFixed(1)}M`
                          : platform.follower_count >= 1000
                          ? `${(platform.follower_count / 1000).toFixed(1)}K`
                          : platform.follower_count?.toLocaleString() || '0'} followers
                        {platform.engagement_rate ? ` · ${platform.engagement_rate.toFixed(1)}% engagement` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {platform.is_verified && (
                      <CheckCircle size={16} className="text-success" />
                    )}
                    <ExternalLink size={14} className="text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-gradient-to-br from-primary/10 via-card to-card border border-primary/30 rounded-xl p-6 text-center">
          <p className="text-sm text-muted-foreground mb-3">Want to work with {userProfile?.full_name?.split(' ')[0]}?</p>
          <Link
            href="/"
            className="inline-flex items-center gap-2 bg-gradient-purple text-white px-6 py-3 rounded-xl font-600 text-sm hover:opacity-90 transition-opacity"
          >
            Post a Drop on Blow →
          </Link>
        </div>
      </div>
    </div>
  );
}
